import { createFileRoute, Link } from "@tanstack/react-router";
import { PartyPopper, ArrowRight } from "lucide-react";
import { useCategory } from "@/lib/use-catalog";
import { CategoryGrid } from "@/components/site/CategoryGrid";

export const Route = createFileRoute("/hoppborgar")({
  head: () => ({
    meta: [
      { title: "Hoppborgar & uppblåsbart — Event Sverige" },
      { name: "description", content: "Hyr hoppborgar, hinderbanor, sumo-dräkter och rutschkanor till festen, skolavslutningen eller företagseventet. Kostnadsfri intresseanmälan." },
      { property: "og:title", content: "Hoppborgar & uppblåsbart — Event Sverige" },
      { property: "og:description", content: "Uppblåsbara attraktioner för alla tillfällen — från små kalas till stora festivaler." },
      { property: "og:url", content: "/hoppborgar" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/hoppborgar" }],
  }),
  component: HoppborgarPage,
});

function HoppborgarPage() {
  const { data, isLoading } = useCategory("hoppborgar");
  const products = data?.products ?? [];

  return (
    <div>
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <PartyPopper className="h-3.5 w-3.5" /> Hoppborgar & uppblåsbart
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95]">
              Uppblåsbar<br /><span className="text-accent">spelglädje.</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 max-w-xl">
              Klassiska hoppborgar, hinderbanor, sumo-brottning och tvilling-rutschkanor.
              Perfekt för kalas, skolavslutningar, festivaler och företagsevent.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 px-3 py-1 font-semibold">
                SS-EN 14960-1:2019
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 px-3 py-1 font-semibold">
                Försäkrat
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 px-3 py-1 font-semibold">
                Rigg & förankring som tillval
              </span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/produkter" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 transition">
                Se hela sortimentet <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/forfragan" className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 text-primary-foreground border border-primary-foreground/20 px-6 py-3 font-semibold hover:bg-primary-foreground/15 transition">
                Gå till förfrågan
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <CategoryGrid products={products} isLoading={isLoading} placeholder="Sök i hoppborgar..." />

        <div className="mt-16 rounded-2xl border border-border bg-card p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-xl font-bold">Fler kategorier att utforska</h2>
              <p className="mt-1 text-sm text-muted-foreground">Komplettera eventet med tivolispel eller ljud & ljus.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/tivolispel" className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/70 transition">Tivolispel <ArrowRight className="h-3.5 w-3.5" /></Link>
              <Link to="/scen-ljud-ljus" className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold hover:bg-secondary/70 transition">Scen & ljud <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
